import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import userService from '../../services/userService';
import artistContentService from '../../services/artistContentService';
import { getBucketForCategory, getSignedImageUrl } from '../../utils/imageUpload';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import ArtistHeader from './components/ArtistHeader';
import ContentSectionCard from './components/ContentSectionCard';

import ValidationIndicators from './components/ValidationIndicators';
import EditorActions from './components/EditorActions';
import { Activity, Users } from 'lucide-react';
import realtimeService from '../../services/realtimeService';

export default function ArtistDetailAdminView() {
  const { user, userProfile, loading } = useAuth();
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const artistId = searchParams?.get('artistId');
  
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [artistData, setArtistData] = useState(null);
  const [artistContent, setArtistContent] = useState({
    profile: null,
    promotion: null,
    about: null,
    calendar: [],
    work: [],
    shop: []
  });
  const [expandedSections, setExpandedSections] = useState(new Set(['profile']));
  const [contentLoading, setContentLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [triggerSectionEdit, setTriggerSectionEdit] = useState(null);
  const [realtimeStatus, setRealtimeStatus] = useState({
    connected: false,
    activeViewers: 0,
    lastUpdate: null
  });

  // Remove forced dark mode - use ThemeContext instead
  useEffect(() => {
    // Theme is now managed by ThemeContext, no need to force it
  }, []);

  // Load artist data and content on component mount
  useEffect(() => {
    if (user && userProfile?.role === 'admin' && artistId) {
      loadArtist();
    } else if (!artistId) {
      setError('Artist ID is required to view artist details.');
    }
  }, [user, userProfile, artistId]);

  // Real-time subscription for artist profile changes
  useEffect(() => {
    if (!artistId) return;

    let unsubscribe;

    const setupRealtime = async () => {
      try {
        // Subscribe to changes for this specific artist
        unsubscribe = realtimeService?.subscribeToUserProfiles((change) => {
          if (change?.profile?.id === artistId) {
            setRealtimeStatus(prev => ({
              ...prev,
              connected: true,
              lastUpdate: new Date()
            }));

            // Reload artist data if updated by another admin
            if (change?.event === 'UPDATE') {
              loadArtist();
            }
          }
        });
      } catch (err) {
        console.error('Real-time subscription error:', err);
      }
    };

    setupRealtime();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [artistId]);

  const loadArtist = async () => {
    try {
      setContentLoading(true);
      setError(null);
      
      // Load basic artist profile data
      const profile = await userService?.getUserById(artistId);
      if (!profile) {
        throw new Error('Artist not found');
      }
      setArtistData(profile);

      // SYNCHRONIZATION FIX: Use the same service methods as artist view
      // Load all artist content sections with consistent data fetching
      const [aboutData, promotionsData, eventsData, worksData] = await Promise.all([
        artistContentService?.getArtistAbout(artistId),
        artistContentService?.getArtistPromotions(artistId),
        artistContentService?.getArtistEvents(artistId),
        artistContentService?.getArtistWorks(artistId)
      ]);

      // OBJECT NOT FOUND FIX: Only generate signed URLs for existing files
      let processedPromotions = promotionsData;
      if (promotionsData) {
        const bucket = getBucketForCategory('promotion');
        
        // First, check which files actually exist before generating signed URLs
        const imageFields = [
          { pathKey: 'splash_image_path', urlKey: 'splashImageUrl' },
          { pathKey: 'home_image_path', urlKey: 'homeImageUrl' },
          { pathKey: 'logo_image_path', urlKey: 'logoImageUrl' }
        ];

        const signedUrls = {};
        
        for (const field of imageFields) {
          if (promotionsData?.[field?.pathKey]) {
            try {
              // Check if file exists first
              const { data: fileExists, error: fileError } = await supabase?.storage
                ?.from(bucket)
                ?.list('', {
                  search: promotionsData?.[field?.pathKey]?.split('/')?.pop()
                });
              
              if (fileError) {
                console.warn(`Error checking file existence for ${field?.pathKey}:`, fileError);
                signedUrls[field?.urlKey] = null;
                continue;
              }

              // If file exists, generate signed URL
              if (fileExists && fileExists?.length > 0) {
                const result = await getSignedImageUrl(bucket, promotionsData?.[field?.pathKey], 7200);
                
                if (result?.data?.signedUrl) {
                  signedUrls[field?.urlKey] = result?.data?.signedUrl;
                } else {
                  console.warn(`Failed to generate signed URL for ${field?.pathKey}:`, result?.error);
                  signedUrls[field?.urlKey] = null;
                }
              } else {
                // File doesn't exist, set to null instead of throwing error
                console.warn(`File not found in storage for ${field?.pathKey}: ${promotionsData?.[field?.pathKey]}`);
                signedUrls[field?.urlKey] = null;
              }
            } catch (error) {
              // Graceful error handling - don't let missing images break the entire view
              console.warn(`Error processing image for ${field?.pathKey}:`, error);
              signedUrls[field?.urlKey] = null;
            }
          } else {
            signedUrls[field?.urlKey] = null;
          }
        }
        
        processedPromotions = {
          ...promotionsData,
          ...signedUrls
        };
      }

      // Process work images with same error-resistant approach
      let processedWorks = worksData || [];
      if (processedWorks?.length > 0) {
        const workBucket = getBucketForCategory('work');
        
        const workImagePromises = processedWorks?.map(async (work) => {
          if (work?.image_path) {
            try {
              // Check if file exists first
              const { data: fileExists, error: fileError } = await supabase?.storage
                ?.from(workBucket)
                ?.list('', {
                  search: work?.image_path?.split('/')?.pop()
                });
              
              if (fileError || !fileExists || fileExists?.length === 0) {
                console.warn(`Work image file not found: ${work?.image_path}`);
                return {
                  ...work,
                  imageUrl: null
                };
              }

              // File exists, generate signed URL
              const result = await getSignedImageUrl(workBucket, work?.image_path, 7200);
              return {
                ...work,
                imageUrl: result?.data?.signedUrl || null
              };
            } catch (error) {
              console.warn(`Error processing work image for ${work?.id}:`, error);
              return {
                ...work,
                imageUrl: null
              };
            }
          }
          return work;
        });

        processedWorks = await Promise.all(workImagePromises);
      }

      setArtistContent({
        profile: profile,
        promotion: processedPromotions,
        about: aboutData,
        calendar: eventsData || [],
        work: processedWorks,
        shop: processedWorks?.filter(work => work?.include_in_shop) || []
      });

      // CACHE SYNCHRONIZATION: Force refresh of artist's own cached data
      // This ensures when artist checks their profile, they see the same data
      if (typeof window !== 'undefined' && window.localStorage) {
        const cacheKey = `artist_data_${artistId}`;
        window.localStorage?.removeItem(cacheKey);
      }

    } catch (error) {
      console.error('Error loading artist data:', error);
      setError(`Failed to load artist data: ${error?.message}`);
    } finally {
      setContentLoading(false);
    }
  };

  const handleSectionToggle = (section) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded?.has(section)) {
      newExpanded?.delete(section);
    } else {
      newExpanded?.add(section);
    }
    setExpandedSections(newExpanded);
  };

  // Handle section clicks from ValidationIndicators
  const handleSectionClick = (sectionKey) => {
    setTriggerSectionEdit(sectionKey);
    
    // Ensure the section is expanded
    if (!expandedSections?.has(sectionKey)) {
      handleSectionToggle(sectionKey);
    }
    
    // Clear the trigger after a short delay to allow for repeated clicks
    setTimeout(() => {
      setTriggerSectionEdit(null);
    }, 1000);
  };

  const handleContentUpdate = async (section, updates) => {
    try {
      setSaving(true);
      setError(null);

      let updatedData;
      switch (section) {
        case 'profile':
          updatedData = await userService?.updateUser(artistId, updates);
          setArtistData({ ...artistData, ...updatedData });
          break;
        case 'about':
          if (updates?._action === 'delete') {
            await artistContentService?.deleteArtistAbout(artistId);
            updatedData = null;
          } else {
            updatedData = await artistContentService?.updateArtistAbout(artistId, updates);
          }
          break;
        case 'promotion':
          if (updates?._action === 'delete') {
            await artistContentService?.deleteArtistPromotions(artistId);
            updatedData = null;
          } else {
            updatedData = await artistContentService?.updateArtistPromotions(artistId, updates);
          }
          break;
        case 'calendar':
          if (updates?._action === 'delete') {
            await artistContentService?.deleteArtistEvent(updates?.id);
          } else if (updates?.id && updates?.id !== 'new') {
            updatedData = await artistContentService?.updateArtistEvent(updates?.id, updates);
          } else {
            updatedData = await artistContentService?.createArtistEvent(artistId, updates);
          }
          break;
        case 'work': case'shop': // Shop items are works with include_in_shop flag
          if (updates?._action === 'delete') {
            await artistContentService?.deleteArtistWork(updates?.id);
          } else if (updates?.id && updates?.id !== 'new') {
            updatedData = await artistContentService?.updateArtistWork(updates?.id, updates);
          } else {
            updatedData = await artistContentService?.createArtistWork(artistId, updates);
          }
          break;
        default:
          throw new Error(`Unknown section: ${section}`);
      }

      // Reload content to reflect changes with fresh signed URLs
      await loadArtist();
      
    } catch (error) {
      console.error('Error updating content:', error);
      setError(`Failed to update ${section}: ${error?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAccountAction = async (action) => {
    try {
      setSaving(true);
      setError(null);

      switch (action) {
        case 'activate':
          await userService?.updateUser(artistId, { status: 'active' });
          break;
        case 'suspend':
          await userService?.updateUser(artistId, { status: 'suspended' });
          break;
        case 'deactivate':
          await userService?.updateUser(artistId, { status: 'inactive' });
          break;
        case 'reset-password':
          await userService?.resendInvitation(artistId, artistData?.email);
          alert(`Password reset email sent to ${artistData?.email}`);
          return;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      // Reload artist data to reflect status change
      await loadArtist();
      
    } catch (error) {
      console.error('Error performing account action:', error);
      setError(`Failed to ${action} account: ${error?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const contentSections = [
    {
      key: 'profile',
      title: 'Artist Profile',
      icon: 'User',
      description: 'Basic information, contact details, and account settings',
      content: artistContent?.profile,
      validation: {
        complete: !!artistContent?.profile?.full_name && !!artistContent?.profile?.email,
        required: ['full_name', 'email', 'artist_type']
      }
    },
    {
      key: 'promotion',
      title: 'Promotion Materials',
      icon: 'Star',
      description: 'Splash screens, logos, and promotional assets',
      content: artistContent?.promotion,
      validation: {
        complete: !!artistContent?.promotion?.splash_image_path,
        required: ['splash_image_path']
      }
    },
    {
      key: 'about',
      title: 'About Section',
      icon: 'FileText',
      description: 'Biography, header images, and booking contact information',
      content: artistContent?.about,
      validation: {
        complete: !!artistContent?.about?.about_text,
        required: ['about_text']
      }
    },
    {
      key: 'calendar',
      title: 'Calendar Events',
      icon: 'Calendar',
      description: `${artistContent?.calendar?.length || 0} events scheduled`,
      content: artistContent?.calendar,
      validation: {
        complete: artistContent?.calendar?.length > 0,
        required: []
      }
    },
    {
      key: 'work',
      title: 'Portfolio Works',
      icon: 'Briefcase',
      description: `${artistContent?.work?.length || 0} portfolio items`,
      content: artistContent?.work,
      validation: {
        complete: artistContent?.work?.length > 0,
        required: []
      }
    },
    {
      key: 'shop',
      title: 'Shop Items',
      icon: 'ShoppingBag',
      description: `${artistContent?.shop?.length || 0} items available for purchase`,
      content: artistContent?.shop,
      validation: {
        complete: artistContent?.shop?.length > 0,
        required: []
      }
    }
  ];

  // Calculate completion percentage
  const completionPercentage = Math.round(
    (contentSections?.filter(section => section?.validation?.complete)?.length / 
     contentSections?.length) * 100
  );

  // Show loading state while authentication is being checked
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex">
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={setIsSidebarCollapsed}
        />
        <main className="flex-1 p-8 ml-64">
          <div className="max-w-7xl mx-auto">
            
            {/* Real-time Sync Indicator - Updated colors */}
            {realtimeStatus?.connected && (
              <div className="mb-4 bg-card rounded-lg shadow-sm border border-success p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 rounded-full bg-success animate-pulse"></div>
                    <Activity size={16} className="text-success" />
                    <span className="text-sm font-medium text-text-primary">
                      Real-time sync active
                    </span>
                  </div>
                  {realtimeStatus?.lastUpdate && (
                    <span className="text-xs text-text-secondary">
                      Last synced: {realtimeStatus?.lastUpdate?.toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Back Button - Updated colors */}
            <Button
              onClick={() => navigate('/admin-dashboard')}
              variant="outline"
              className="mb-6 hover:bg-muted transition-colors"
            >
              <Users size={16} className="mr-2" />
              Back to Dashboard
            </Button>

            {/* Artist Header */}
            <ArtistHeader 
              artistData={artistData}
              completionPercentage={completionPercentage}
              onAccountAction={handleAccountAction}
              saving={saving}
            />

            {/* Error Message Display - Updated colors */}
            {error && (
              <div className="bg-destructive/10 border border-destructive rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Icon name="AlertTriangle" className="text-destructive mt-1 flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <h4 className="text-destructive font-medium">Error Loading Artist Content</h4>
                    <p className="text-destructive-foreground mt-1 text-sm">{error}</p>
                    <div className="mt-3 flex gap-2">
                      <button 
                        onClick={() => setError(null)}
                        className="px-3 py-1 bg-destructive/20 hover:bg-destructive/30 text-destructive rounded text-sm font-medium transition-colors"
                      >
                        Dismiss
                      </button>
                      <button 
                        onClick={loadArtist}
                        className="px-3 py-1 bg-destructive/20 hover:bg-destructive/30 text-destructive rounded text-sm font-medium transition-colors"
                        disabled={contentLoading}
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Breadcrumb Navigation - Updated colors */}
            <nav className="flex items-center space-x-2 text-sm text-text-secondary mb-6">
              <button 
                onClick={() => navigate('/admin-dashboard')}
                className="hover:text-text-primary transition-colors"
              >
                Admin Dashboard
              </button>
              <Icon name="ChevronRight" size={16} />
              <span className="text-text-primary">Artist Detail View</span>
            </nav>

            {contentLoading ? (
              <div className="space-y-6">
                <div className="bg-card rounded-lg border border-border p-8 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-text-secondary">Loading artist content...</p>
                </div>
              </div>
            ) : artistData ? (
              <>
                {/* Validation Indicators */}
                <ValidationIndicators 
                  sections={contentSections}
                  completionPercentage={completionPercentage}
                  onSectionClick={handleSectionClick}
                />

                {/* Content Sections */}
                <div className="space-y-4">
                  {contentSections?.map((section) => (
                    <ContentSectionCard
                      key={section?.key}
                      section={section}
                      isExpanded={expandedSections?.has(section?.key)}
                      onToggle={() => handleSectionToggle(section?.key)}
                      onUpdate={(updates) => handleContentUpdate(section?.key, updates)}
                      saving={saving}
                      triggerEdit={triggerSectionEdit}
                      artistId={artistId}
                    />
                  ))}
                </div>

                {/* Editor Actions */}
                <EditorActions
                  artistId={artistId}
                  artistData={artistData}
                  onContentReload={loadArtist}
                />
              </>
            ) : (
              <div className="bg-card rounded-lg border border-border p-12 text-center">
                <Icon name="UserX" size={48} className="text-text-secondary mx-auto mb-4" />
                <h3 className="text-lg font-medium text-text-primary mb-2">Artist Not Found</h3>
                <p className="text-text-secondary mb-4">
                  The artist with ID "{artistId}" could not be found or you don't have permission to view it.
                </p>
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin-dashboard')}
                  iconName="ArrowLeft"
                  iconPosition="left"
                  className="hover:bg-muted transition-colors"
                >
                  Back to Dashboard
                </Button>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}